// External links open in a new browser WINDOW, not a tab (2026-10-22).
//
// installExternalLinkWindows() adds one click listener on the document, in the
// capture phase, so it sees every link: MUI <Link target="_blank">, a plain
// <a>, and the links inside dangerouslySetInnerHTML content (email bodies,
// templates, step links, tracking links). A plain left click on a link whose
// destination is an http(s) URL on another origin opens a centered pop-up
// window sized to 80% of the screen. A new window every click (no shared
// window name).
//
// Never intercepted: Ctrl/Cmd/Shift/Alt clicks and the middle button (the
// browser's own tab/window behavior stays), same-origin routes (/contact/<n>,
// /flows/.../flowchart, admin pages), a link with a download attribute,
// blob:/data:/mailto:/tel: URLs (attachments, ImageLightbox, invoice blobs),
// and a link inside an editable area (the reply and template editors, where a
// click places the cursor).
//
// Keyboard: Enter on a focused <a> makes the browser fire a click event
// (button 0, no modifiers), so Enter follows the same rule through this same
// listener. The e2e suite checks that in Chromium.
//
// Pop-up-blocked detection and opener safety. window.open() with "noopener"
// or "noreferrer" in its features returns null in Chrome and Edge even when
// the window opened (the spec says so), so a null return could not tell a
// blocked pop-up from a working one. So the window opens WITHOUT those two
// features, and:
// - a null return (or a throw) means the browser blocked it: fall back to a
//   new tab with "noopener,noreferrer", which needs no handle;
// - otherwise the returned window's opener is set to null at once. The new
//   window is still on its initial about:blank, same-origin with CXHub, so
//   the setter works, and it runs before the external page loads, so that
//   page never sees window.opener. If the setter throws, the window is closed
//   and the tab fallback opens instead: an external page is never left with
//   an opener.
// Referrer: without "noreferrer", the document's referrer policy decides.
// The server sends Referrer-Policy: no-referrer on every response (helmet's
// default, server/src/server.js), and ui/index.html carries
// <meta name="referrer" content="no-referrer"> for the dev and e2e builds,
// so no Referer reaches the external site.

// The URL to open in a window for this click, or null when the click is left
// to the browser.
export function externalLinkTarget(event, origin) {
  if (!event || event.defaultPrevented) return null
  if (event.button !== 0) return null
  if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return null
  const start = event.target
  const a = typeof start?.closest === 'function' ? start.closest('a[href]') : null
  if (!a) return null
  if (a.hasAttribute('download')) return null
  if (a.isContentEditable || a.closest('[contenteditable=""], [contenteditable="true"]')) return null
  let url
  try {
    url = new URL(a.getAttribute('href'), a.baseURI || origin)
  } catch {
    return null
  }
  // http(s) only: blob:, data:, mailto: and tel: stay with the browser.
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null
  if (url.origin === origin) return null
  return url.href
}

// The pop-up features: 80% of the available screen, centered on the screen
// CXHub is on.
export function popupFeatures(win) {
  const s = win.screen ?? {}
  const availW = s.availWidth || 1280
  const availH = s.availHeight || 800
  const w = Math.round(availW * 0.8)
  const h = Math.round(availH * 0.8)
  const x = Math.round((s.availLeft ?? 0) + (availW - w) / 2)
  const y = Math.round((s.availTop ?? 0) + (availH - h) / 2)
  return `popup=yes,width=${w},height=${h},left=${x},top=${y}`
}

function openInTab(win, url) {
  try {
    win.open(url, '_blank', 'noopener,noreferrer')
  } catch {
    // Nothing more to try: the click was already prevented.
  }
}

// Opens url in a new window, or in a new tab when a pop-up blocker stops the
// window. Returns 'window' or 'tab'.
export function openExternalWindow(win, url) {
  let popup = null
  try {
    popup = win.open(url, '_blank', popupFeatures(win))
  } catch {
    popup = null
  }
  if (!popup) {
    openInTab(win, url)
    return 'tab'
  }
  try {
    popup.opener = null
  } catch {
    try { popup.close() } catch { /* already gone */ }
    openInTab(win, url)
    return 'tab'
  }
  return 'window'
}

// Installs the listener once. Returns a function that removes it (tests).
export function installExternalLinkWindows(doc = globalThis.document, win = doc?.defaultView ?? globalThis.window) {
  if (!doc || !win) return () => {}
  function onClick(event) {
    const url = externalLinkTarget(event, win.location.origin)
    if (!url) return
    event.preventDefault()
    openExternalWindow(win, url)
  }
  doc.addEventListener('click', onClick, true)
  return () => doc.removeEventListener('click', onClick, true)
}
